import { gql, ObservableQuery } from '@apollo/client'
import { useApolloClient } from '@apollo/client/react'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React, { JSX, useEffect, useMemo, useRef, useState } from 'react'
import NavBar from 'src/components/NavBar'
import { getEffectiveSpaceTypes } from 'src/components/SearchBar/SpaceTypeFilters'
import { iOS } from 'src/constants/device'
import { useFilterProvider } from 'src/context/FilterProvider'
import { useDelayedLoad } from 'src/hooks/useDelayedLoad'
import { useGetPrice } from 'src/hooks/usePrice'
import useUserLocation from 'src/hooks/useUserLocation'
import { ICoordinates, ILocation, IQuery } from 'src/types/graphql'
import {
  buildDirectoryFichaHref,
  directoryLocationTypesForSpaceTypes
} from 'src/utils/directory'
import { canonicalCity, canonicalHood } from 'src/utils/pixels/common'
import {
  trackDirectoryCardClick,
  trackDirectoryMapImpression
} from 'src/utils/pixels/directoryEvents'
import { getVertical } from 'src/utils/pixels/enrichment'
import fbp from 'src/utils/pixels/fbp'
import ga from 'src/utils/pixels/gtag'
import {
  trackMapInteraction,
  trackSearchResultView
} from 'src/utils/pixels/searchEvents'
import {
  bucketResultsCount,
  buildCardBadges,
  pillarFromPath
} from 'src/utils/searchAnalytics'
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  MAP_PAGE_SIZE,
  MAX_PUBLIC_PIN_ZOOM,
  NEAR_USER,
  SEARCH_GRID_SIZE
} from '../../constants'
import { CARD_LOCATION_FIELDS } from '../../constants/fragments'
import { Colors } from '../../constants/theme'
import { useLocations } from '../../context/LocationsProvider'
import useBreakpoints from '../../hooks/useBreakpoints'
import BaseButton from '../Buttons/BaseButton'
import { useDirectoryLocations } from '../DirectoryFacility/useDirectoryLocations'
import FlexBox from '../FlexBox'
import { Vsb } from '../Hidden'
import { AdminHostBadgesProvider } from './AdminHostBadges'
import SearchList from './SearchList'
import { MapContainer, StyledContainer, StyledSwipeableDrawer } from './Styled'

const Filters = dynamic(() => import('../Filters'))
const MapWrapper = dynamic(() => import('../Map/MapWrapper'))
const Pagination = dynamic(() => import('../Pagination'))
const Footer = dynamic(() => import('../Footer'))

// Module-scoped cache of the last-rendered grid keyed by URL. Survives
// SearchManager remounts within the same tab session, so back-nav from a
// listing paints cards on the very first frame instead of flashing the
// skeleton while Apollo's cache lookup resolves on first render. Bounded
// by the number of unique URLs the user visits in a session — fine for
// a search/discover surface.
//
// Strip params that are scroll/UI-context (`sliderOpen`, `scrollY`,
// `page`) from the key so the cache hits across the URL-rewrite the card
// click handler does to persist drawer + scroll state. Without this, the
// first paint after back-nav misses the cache and a skeleton frame slips
// through.
const renderedLocationsCache = new Map<string, ILocation[]>()
const CACHE_KEY_IGNORED_PARAMS = ['sliderOpen', 'scrollY']
const cacheKeyFromLocation = (): string | undefined => {
  if (typeof window === 'undefined') return undefined
  const params = new URLSearchParams(window.location.search)
  for (const k of CACHE_KEY_IGNORED_PARAMS) params.delete(k)
  // Sort so equivalent param sets produce the same key regardless of
  // insertion order (URLSearchParams preserves insertion order otherwise).
  params.sort()
  const search = params.toString()
  return window.location.pathname + (search ? `?${search}` : '')
}

// Map-only top-up: returns just `hits` for marker rendering. No `total` (the
// SSG payload already provides it) and no `googlePlaceDetails` (already on
// initialData). `fetchPolicy: 'no-cache'` on the call site keeps Apollo's
// store untouched so this can't accidentally retrigger a subscribed query.
const MAP_TOP_UP_LOCATIONS = gql`
  ${CARD_LOCATION_FIELDS}
  query SearchLocationsMapTopUp(
    $pagination: PaginationInput!
    $searchInput: SearchLocationsInput!
  ) {
    searchLocations(pagination: $pagination, searchInput: $searchInput) {
      hits {
        ...CardLocationFields
      }
    }
  }
`

// The top-up waits for the landing to settle: the first scroll / tap /
// keypress, or this many ms, whichever comes first (`useDelayedLoad`). It
// used to fire on the first idle callback after hydration, which on a cheap
// Android lands right inside the window where the reader takes their first
// tap — a ~150 kB CardLocationFields payload for up to 100 listings (photo
// arrays included) to download and parse, plus the SearchManager re-render
// `setExtraMapHits` triggers, all competing with that interaction. Nothing on
// screen needs it that early: the map it feeds sits behind the open drawer on
// a mobile landing, and the paginator that reads `allLocations` is a scroll
// away. Desktop is unaffected in practice — the first mouse move settles it.
const MAP_TOP_UP_SETTLE_MS = 5000

type IProps = {
  header?: JSX.Element
  seoFooter?: JSX.Element
  initialData?: ObservableQuery.Result<
    Pick<IQuery, 'searchLocations' | 'googlePlaceDetails'>
  >
  nearbySearchData?: import('./hostCtaQuery').NearbySearchCountsResult
  locationName?: string
  priorityImages?: boolean
  /** SSR-fetched directory rows for a ZERO-SUPPLY city page (2026-08-28). */
  initialDirectory?: ILocation[]
}

/**
 * URL params that override the pillar defaults baked into a static page's
 * `initialData`. Must stay in sync with the override merge at the bottom of
 * FilterProvider's `filters` memo — same four keys, same reason.
 */
const FILTER_OVERRIDE_PARAMS = ['sizes', 'minPrice', 'maxPrice', 'sortBy']

function SearchManager({
  header,
  seoFooter,
  initialData: bakedInitialData,
  nearbySearchData,
  locationName,
  priorityImages,
  initialDirectory
}: IProps) {
  const { t } = useTranslation('common')
  const { getIsBrokerMode } = useGetPrice()
  const [mounted, setMounted] = useState(false)

  // ISR landing reconciliation (2026-08-31). The pillar/city/hood pages are
  // statically generated again, so `bakedInitialData` is always the
  // PILLAR-DEFAULT result set — the prerender cache ignores the query string.
  // A landing URL carrying a user filter override (`?sizes=…` — indexed by
  // Google and present on some ad finals; ~17% of pillar landings) must not
  // trust it: drop the baked data and let the normal /search fetch path run
  // (default paint → skeleton → filtered results). This is the client half of
  // the fix for the April-2026 "deep links silently rendered the unfiltered
  // list" bug; the other half is FilterProvider's URL-override merge.
  //
  // Read from `window.location.search`, NOT `router.query` — after a
  // routing-proxy rewrite `router.query` carries only what the server saw
  // (the <UTMs /> asPath lesson, docs/claude/marketing-attribution.md), and
  // this check must never silently stop seeing overrides. Starts `false` so
  // the first client render matches the SSR HTML (hydration-safe); the
  // effect re-runs on `asPath` so client-side navs between pillar pages
  // re-evaluate — landing filtered then crosslinking to a clean pillar page
  // must re-honor that page's baked data.
  const { asPath } = useRouter()
  const [urlOverridesBaked, setUrlOverridesBaked] = useState(false)
  useEffect(() => {
    if (!bakedInitialData) return
    const params = new URLSearchParams(window.location.search)
    setUrlOverridesBaked(FILTER_OVERRIDE_PARAMS.some((p) => params.has(p)))
  }, [bakedInitialData, asPath])
  const initialData = urlOverridesBaked ? undefined : bakedInitialData
  const { isUpMd: rawIsUpMd } = useBreakpoints()
  // Defer breakpoint + browser-only values until after hydration to avoid SSR mismatch.
  // Server always renders the mobile layout (isUpMd=false); client snaps to real value after mount.
  const isUpMd = mounted && rawIsUpMd
  const isIOS = mounted && iOS

  // Desktop: the left rail is its own scroll container (`md:h-full
  // md:overflow-y-auto` on the rail div + bounded `StyledContainer`).
  // Mobile: the drawer's inner div owns scroll.
  const refDesktop = React.useRef<HTMLDivElement>(null)
  const refMobile = React.useRef<HTMLDivElement>(null)
  // The mobile drawer renders inline (`disablePortal`, below), so MUI's
  // ModalManager must scope to the results grid, not <body>: with <body> it
  // aria-hides the siblings of every ancestor, which takes the mobile nav
  // (logo, search pill, "Filtros") out of the accessibility tree while the
  // sheet is open. Scoped here it hides only the desktop rail and the map
  // behind the sheet. The scroll lock lands on the grid too, which is fine:
  // the mobile page is one viewport tall. Only ever called client-side, in
  // ModalManager's effect, so `document` is safe and no `mounted` gate is
  // needed — gating it made the Modal register against <body> on its first
  // effect, before `mounted` flipped. Measured 2026-10-28, see
  // docs/superpowers/specs/2026-10-28-mobile-results-sheet-ssr-design.md.
  const sheetContainerRef = React.useRef<HTMLDivElement>(null)
  const container = () => sheetContainerRef.current ?? document.body

  const {
    placeDetails: userLocation,
    center: effectiveCenter,
    zoom: locationZoom
  } = useUserLocation()
  const {
    filters,
    updateParams,
    shallowUpdateParams,
    isReady,
    isSliderOpen,
    setIsSliderOpen
  } = useFilterProvider()

  const { loading, requested, selectedLocation, setSelectedLocation, ...rest } =
    useLocations({
      skip: !!initialData,
      userLocation
    })

  // Silent top-up on landing pages. SSG ships SSG_PAGE_SIZE (12) hits which
  // already fills SEARCH_GRID_SIZE so first paint is grid-clean. This effect
  // fetches up to MAP_PAGE_SIZE more in the background so the MAP gets a
  // denser marker set than the 12-listing grid alone provides — dense
  // markets (CDMX/MTY self-storage) typically have 50+ listings worth
  // surfacing on the map even when the visible grid stays at 12.
  // Constraints (deliberate):
  //   - Only fires when initialData is present (i.e. /[pillar]/[city]/[hood]
  //     and /[pillar]/[city]). On /search, useLocations already pulls 50.
  //   - Fires ONCE per mount (ref guard), captures filter snapshot at fire
  //     time so subsequent FilterProvider updates don't refire/re-shape it.
  //   - Imperative client.query (not useQuery) — no subscription, no deps
  //     array drama, no analytics side-effects from useLocations.
  //   - fetchPolicy 'no-cache' — doesn't pollute Apollo store, can't
  //     accidentally trigger a refetch in any subscribed query.
  //   - Waits for the landing to settle (MAP_TOP_UP_SETTLE_MS) and then for
  //     an idle callback, so it never competes with first paint or the first
  //     tap. Aborted on unmount (e.g. user types in SearchBar and the page
  //     navigates to /search before the top-up lands).
  //   - Skipped on saveData / 2g connections.
  //   - Result merged into allLocations; the grid derives its slice from
  //     allLocations on initialData branches, so the grid grows from 9 → 12
  //     once (idle-time, below-fold append) — never re-orders or shrinks.
  const apollo = useApolloClient()
  const [extraMapHits, setExtraMapHits] = useState<ILocation[]>([])
  const topUpFiredRef = useRef(false)
  const landingSettled = useDelayedLoad(MAP_TOP_UP_SETTLE_MS)
  useEffect(() => {
    if (!initialData || !isReady || !landingSettled || topUpFiredRef.current)
      return
    const initialHits = (initialData.data?.searchLocations?.hits ??
      []) as ILocation[]
    const total = initialData.data?.searchLocations?.total ?? 0
    if (initialHits.length >= Math.min(total, MAP_PAGE_SIZE)) return

    type NavWithConn = Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string }
    }
    const conn =
      typeof navigator !== 'undefined'
        ? (navigator as NavWithConn).connection
        : undefined
    if (
      conn?.saveData ||
      conn?.effectiveType === '2g' ||
      conn?.effectiveType === 'slow-2g'
    )
      return

    topUpFiredRef.current = true

    // Snapshot filters/center/place at fire time. Reading these once here
    // prevents the effect from being deps-array-reactive to every URL/filter
    // mutation that FilterProvider emits while the user is on the page.
    const placeIdRaw = filters.placeId
    const isNearUser = placeIdRaw === NEAR_USER
    const placeIdForVars = !isNearUser ? placeIdRaw : undefined
    const queryCenter =
      filters.lat && filters.lng
        ? { lat: filters.lat, lng: filters.lng }
        : undefined
    const placeDetailsCenter = initialData.data?.googlePlaceDetails
      ? {
          lat: initialData.data.googlePlaceDetails.lat,
          lng: initialData.data.googlePlaceDetails.lng
        }
      : undefined
    const center = isNearUser
      ? (userLocation ?? DEFAULT_CENTER)
      : (queryCenter ??
        placeDetailsCenter ??
        (placeIdRaw ? undefined : DEFAULT_CENTER))

    const variables = {
      pagination: { from: 0, pageSize: MAP_PAGE_SIZE },
      searchInput: {
        placeId: placeIdForVars,
        center: center ? { lat: center.lat, lng: center.lng } : undefined,
        zoom: filters.zoom ?? DEFAULT_ZOOM,
        canStoreVehicle: filters.canStoreVehicle,
        sortBy: filters.sortBy,
        sizes: filters.sizes,
        priceRange:
          filters.maxPrice || filters.minPrice
            ? { min: filters.minPrice, max: filters.maxPrice }
            : undefined,
        locationTypes: filters.locationTypes
      }
    }

    const controller = new AbortController()
    let cancelled = false
    let idleHandle: number | undefined
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined

    const run = () => {
      if (cancelled) return
      apollo
        .query<Pick<IQuery, 'searchLocations'>>({
          query: MAP_TOP_UP_LOCATIONS,
          variables,
          fetchPolicy: 'no-cache',
          context: { signal: controller.signal }
        })
        .then(({ data }) => {
          if (cancelled) return
          const fetched = (data?.searchLocations?.hits ?? []) as ILocation[]
          if (!fetched.length) return
          const initialIds = new Set(initialHits.map((h) => h.id))
          const extras = fetched.filter((h) => !initialIds.has(h.id))
          if (!extras.length) return
          setExtraMapHits(extras)
        })
        .catch(() => {
          // Silent failure — top-up is opportunistic. Map gracefully stays
          // at SSG-shipped hits. Don't pollute logger/analytics.
        })
    }

    type WindowWithIdle = Window & {
      requestIdleCallback?: (
        cb: IdleRequestCallback,
        opts?: { timeout?: number }
      ) => number
      cancelIdleCallback?: (handle: number) => void
    }
    const w =
      typeof window !== 'undefined' ? (window as WindowWithIdle) : undefined
    if (w && typeof w.requestIdleCallback === 'function') {
      idleHandle = w.requestIdleCallback(run, { timeout: 2000 })
    } else {
      timeoutHandle = setTimeout(run, 0)
    }

    return () => {
      cancelled = true
      controller.abort()
      if (idleHandle !== undefined && w?.cancelIdleCallback) {
        w.cancelIdleCallback(idleHandle)
      }
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle)
    }
    // initialData reference is stable per page (SSG payload). isReady gates
    // FilterProvider URL hydration. Filters/userLocation are intentionally
    // captured via snapshot above, not deps — see comment block.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isReady, landingSettled])

  const {
    allLocations: rawAllLocations,
    pageLocations: rawPageLocations,
    totalLocations = 0,
    placeDetails,
    mode
  } = initialData
    ? {
        allLocations: initialData?.data?.searchLocations?.hits ?? [],
        pageLocations: initialData?.data?.searchLocations?.hits ?? [],
        totalLocations: initialData?.data?.searchLocations?.total ?? 0,
        placeDetails: initialData?.data?.googlePlaceDetails,
        mode: {}
      }
    : rest

  // Merge top-up hits into the map source only. pageLocations (grid) stays
  // bound to SSG initial set so the listing grid never reflows mid-session.
  // Memoized so identity is stable across renders — without this, the
  // spread on the merge branch produces a new array every render, which
  // re-fires every downstream useMemo (notably obfuscatedMapLocations).
  const allLocations = useMemo<ILocation[] | undefined>(() => {
    const base = rawAllLocations as ILocation[] | undefined
    if (initialData && extraMapHits.length) {
      return [...(base ?? []), ...extraMapHits]
    }
    return base
  }, [rawAllLocations, initialData, extraMapHits])
  const pageLocations = rawPageLocations as ILocation[] | undefined

  // Coordinates arrive pre-obfuscated from the backend `Location.coordinates`
  // field resolver (~80–200m decoy for anyone who isn't the owner / admin /
  // team member). Both the map markers AND the grid cards now carry the decoy,
  // so there's no client-side fuzzing here — re-obfuscating would double-offset
  // the pin. Owner/admin/team viewers receive true coords from the resolver and
  // correctly see real pins.

  useEffect(() => {
    if (!initialData) return
    // results_count + bucket + primary_location_type are primitives so they
    // survive the gtag.ts flat-params filter into PostHog. The spread of
    // `filters` and `mode` carries arrays (locationTypes, sizes, spaceTypes)
    // which get dropped from PostHog's flat-params payload — these explicit
    // primitives are what the supply-density PMF insight slices on.
    ga.event('search_completed', {
      ...filters,
      ...mode,
      spaceTypes: getEffectiveSpaceTypes(filters),
      results_count: totalLocations,
      results_count_bucket: bucketResultsCount(totalLocations),
      primary_location_type: filters.locationTypes?.[0],
      // Override raw `...filters` city/hood with canonical slugs so PostHog
      // groups CDMX variants as one; `pillar` from the route survives
      // zero-result searches. Mirrors the LocationsProvider fire site. See
      // docs/marketing/liquidity.md.
      city: canonicalCity(filters.city),
      hood: canonicalHood(filters.hood),
      pillar: pillarFromPath()
    })
    const hits = initialData?.data?.searchLocations?.hits ?? []
    fbp.event('Search', {
      search_string: filters.search ?? filters.description ?? '',
      content_category: getVertical(
        hits[0]?.defaultConfiguration?.locationType
      ),
      content_type: 'product',
      content_ids: hits
        .slice(0, 5)
        .map((loc) => loc.defaultConfiguration?.id)
        .filter(Boolean)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData])

  const initialPlace =
    placeDetails?.lat && placeDetails?.lng
      ? { lat: placeDetails.lat, lng: placeDetails.lng }
      : filters.lat && filters.lng
        ? { lat: filters.lat, lng: filters.lng }
        : DEFAULT_CENTER
  const [initialCenter, setInitialCenter] = useState<ICoordinates>(
    initialPlace as ICoordinates
  )
  /**
   * Directory rows for the map (plan §2.1, extended 2026-08-23).
   *
   * They render as the map's SUBORDINATE tier — quiet dots under the real
   * listings' price bubbles — never merged into `allLocations`, which stays
   * exactly the ranked, monetized result set the list paginates.
   *
   * Why the map may merge when the LIST must not: a map has no pagination to
   * seam, no filter implying the pins are comparable, and no card shape to
   * make inconsistent. It just answers "what is actually near me" — and in a
   * market with no SpotMe supply that is the difference between a populated
   * map and an empty one.
   *
   * Shares its query with the tier floor (same variables → one request).
   */
  // `useRouter`, never the `next/router` singleton — its getters throw during
  // SSR and would 500 every pillar/city/hood page.
  const router = useRouter()

  const directorySpaceTypes = getEffectiveSpaceTypes(filters)
  // `null` means this vertical publishes NO directory rows (warehouse / nave /
  // land). It must SKIP the query, never fall through to `undefined` — that
  // reads as "no locationType filter" and would answer an industrial-space
  // search with a map full of mini-bodegas.
  const directoryLocationTypes =
    directoryLocationTypesForSpaceTypes(directorySpaceTypes)
  const { locations: directoryMapLocations } = useDirectoryLocations({
    center:
      filters.lat && filters.lng
        ? { lat: filters.lat, lng: filters.lng }
        : undefined,
    zoom: filters.zoom,
    locationTypes: directoryLocationTypes ?? undefined,
    placeId: filters.placeId !== NEAR_USER ? filters.placeId : undefined,
    skip: directoryLocationTypes === null
  })

  // §4.2 exposure for the MAP surface. The dot layer had clicks but no
  // impression (6 of the first 12 directory clicks came from it, with no
  // denominator), so the cannibalization verdict was blind to the one surface
  // that renders ALONGSIDE monetized price bubbles. One session-deduped event
  // with the marker count — never one event per dot (a viewport carries up to
  // 150). Proxy caveat, documented on the helper: "markers were rendered",
  // not "the user looked at the map" — on mobile the drawer can cover it.
  useEffect(() => {
    if (directoryMapLocations.length === 0) return
    trackDirectoryMapImpression(directoryMapLocations.length)
  }, [directoryMapLocations.length])

  // Which directory dot the map should highlight, driven by hovering its card
  // in the list. Separate from `selectedLocation` on purpose — that one opens
  // the floating listing card, which a directory row cannot fill.
  const [hoveredDirectoryId, setHoveredDirectoryId] = useState<string | null>(
    null
  )

  const [mapCenter, setMapCenter] = useState<ICoordinates | null>(null)
  const [mapZoom, setMapZoom] = useState<number>(filters.zoom ?? DEFAULT_ZOOM)

  // Debounce shallow URL pushes driven by the map so rapid camera events
  // (or floating-point drift from programmatic setCenter/setZoom) don't
  // cascade into a URL → filters → query → re-render feedback loop.
  const urlPushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    return () => {
      if (urlPushTimerRef.current) clearTimeout(urlPushTimerRef.current)
    }
  }, [])

  /**
   * Sync Map & Filters
   */

  useEffect(() => {
    // ? Sync User Location
    if (loading || !isReady) return
    if (filters.placeId !== NEAR_USER) return
    if (!effectiveCenter) return
    const { lat, lng } = effectiveCenter
    if (lat === initialCenter.lat && lng === initialCenter.lng) return
    setInitialCenter({ lat, lng })
    setMapZoom(filters.zoom ?? locationZoom ?? DEFAULT_ZOOM)
  }, [loading, isReady, filters, effectiveCenter, locationZoom, initialCenter])

  useEffect(() => {
    // ? Sync Place Location
    if (loading || !isReady) return
    if (!filters.placeId) return
    if (
      filters.placeId !== NEAR_USER &&
      placeDetails?.lat &&
      placeDetails?.lng
    ) {
      const { lat, lng } = placeDetails
      setInitialCenter({ lat: lat!, lng: lng! })
      setMapZoom(placeDetails.zoom ?? filters.zoom ?? DEFAULT_ZOOM)
    }
  }, [placeDetails, filters.placeId, filters.zoom, loading, isReady])

  useEffect(() => {
    if (loading || !isReady) return
    if (initialData) return
    if (!mapCenter && filters.lat && filters.lng) {
      setInitialCenter({ lat: filters.lat, lng: filters.lng })
    }
  }, [filters.lat, filters.lng, initialData, isReady, loading, mapCenter])

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (
      !loading &&
      totalLocations > 0 &&
      pageLocations?.length === 0 &&
      filters.page > 1
    ) {
      updateParams({ page: undefined })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pageLocations, totalLocations, filters.page])

  const pageCount =
    Math.floor(totalLocations / SEARCH_GRID_SIZE) +
    (totalLocations % SEARCH_GRID_SIZE === 0 ? 0 : 1)
  // On initialData branches (pillar/city/hood) the grid slice is derived
  // from allLocations so the idle-time top-up CAN grow the grid up to
  // SEARCH_GRID_SIZE; in practice SSG_PAGE_SIZE === SEARCH_GRID_SIZE so
  // the slice is already full at hydration and the top-up only adds map
  // markers, not new grid tiles. On /search the grid tracks pageLocations
  // which already holds the full server page slice.
  const gridSource = initialData ? allLocations : pageLocations
  const rawLocations = gridSource
    ? gridSource.slice(0, Math.min(gridSource.length, SEARCH_GRID_SIZE))
    : undefined

  // Hold onto the last non-empty render of the grid so brief variable-change
  // refetches don't blank out the cards. Two layers:
  //   - `lastLocationsRef` (within mount): handles map-pan refetches where
  //     Apollo's `data` flips to undefined for ~300ms while the new query
  //     resolves.
  //   - `renderedLocationsCache` (across mounts, module scope): handles the
  //     first frame after back-nav remount, before useQuery has had a chance
  //     to surface its cached value. Without this, the very first commit
  //     renders the skeleton because `data` is briefly undefined.
  const lastLocationsRef = useRef<ILocation[] | undefined>(undefined)
  if (rawLocations && rawLocations.length > 0) {
    lastLocationsRef.current = rawLocations
    const key = cacheKeyFromLocation()
    if (key) renderedLocationsCache.set(key, rawLocations)
  }

  // Search signature. Any change here = treat the next data delivery as a
  // fresh search (Inmuebles24 pattern, not Airbnb): clear held-over cards →
  // show loading skeleton → scroll to top when new results land. Drives
  // three things in lockstep:
  //   1. Clearing `lastLocationsRef` (the held-over fallback during refetch).
  //   2. Disqualifying `initialCacheLookup` (the cross-mount module cache).
  //   3. Firing scroll-to-top once new results land.
  //
  // Included fields:
  //   - `lat` / `lng` / `zoom`: map pan triggers a real refetch — treat it
  //     as a search. The 300ms debounce in the map idle handler is what
  //     prevents this from firing during continuous drag.
  //   - `placeId` when truthy: a user-picked city counts as a new search.
  //     `placeId` when falsy is filtered out — the map idle handler clears
  //     `placeId` on every pan as a side effect (so a stale city slug
  //     doesn't refetch place details), but that clear is paired with the
  //     same lat/lng change that already trips the sig. Including
  //     `placeId: null` would double-trigger only on pans that happen to
  //     have a stale placeId in the URL.
  //
  // Excluded fields:
  //   - `page`: paging keeps the same search, so it must not run the
  //     filter-change effect. It gets its OWN scroll effect below.
  const userFilterSig = useMemo(
    () =>
      JSON.stringify({
        canStoreVehicle: filters.canStoreVehicle,
        sortBy: filters.sortBy,
        sizes: filters.sizes,
        locationTypes: filters.locationTypes,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        placeId: filters.placeId || null,
        lat: filters.lat,
        lng: filters.lng,
        zoom: filters.zoom
      }),
    [
      filters.canStoreVehicle,
      filters.sortBy,
      filters.sizes,
      filters.locationTypes,
      filters.minPrice,
      filters.maxPrice,
      filters.placeId,
      filters.lat,
      filters.lng,
      filters.zoom
    ]
  )

  // Initial-render fallback to the module-scoped cache: serves frames
  // before useQuery has surfaced its cached data. Apollo Client 4 +
  // Next.js route transitions can take several renders before the cached
  // data is emitted to subscribers, so we keep this alive (instead of
  // clearing on first commit) until either real data arrives or the user
  // changes filters.
  const initialCacheLookup = useRef<ILocation[] | undefined>(
    (() => {
      const key = cacheKeyFromLocation()
      return key ? renderedLocationsCache.get(key) : undefined
    })()
  )
  // Snapshot the user-filter signature at mount. If it changes, we stop
  // serving from `initialCacheLookup` (which is keyed to the mount-time
  // URL) so a filter change correctly leads to skeleton → new results
  // rather than briefly showing the prior URL's listings.
  const [mountFiltersSig] = useState(() => userFilterSig)
  const filtersUnchangedSinceMount = mountFiltersSig === userFilterSig

  // Drop the held-over listings on any search-sig change (filter chip OR
  // map pan settle) so the loading skeleton leads the next refetch. The
  // 300ms debounce in the map idle handler is what keeps this from
  // firing during continuous drag.
  const prevUserFilterSigRef = useRef(userFilterSig)
  useEffect(() => {
    if (userFilterSig === prevUserFilterSigRef.current) return
    prevUserFilterSigRef.current = userFilterSig
    lastLocationsRef.current = undefined
  }, [userFilterSig])

  const locations =
    rawLocations && rawLocations.length > 0
      ? rawLocations
      : (lastLocationsRef.current ??
        (filtersUnchangedSinceMount ? initialCacheLookup.current : undefined))

  const hasCachedHits = !!locations && locations.length > 0
  const emptyAndLoading =
    (!requested || loading) && !initialData && !hasCachedHits

  // Scroll the LIST SECTION (not the window) to top when results land for a
  // new search signature. Both surfaces own their own scroll container:
  //   - Desktop: `refDesktop` → the left rail div (`md:h-full md:overflow-y-auto`
  //     inside a viewport-bounded `StyledContainer`). The window doesn't
  //     scroll here — nav + filters + below-fold seoFooter stay put.
  //   - Mobile: `refMobile` → the SwipeableDrawer's inner scrollable div.
  //   - First mount, back-nav: sig unchanged → no fire.
  //   - `loading` gate waits for the refetch to finish so we don't scroll to
  //     an empty/skeleton state before new results paint.
  const scrolledForSigRef = useRef(userFilterSig)
  useEffect(() => {
    if (loading) return
    if (userFilterSig === scrolledForSigRef.current) return
    scrolledForSigRef.current = userFilterSig
    const target = isUpMd ? refDesktop.current : refMobile.current
    target?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [userFilterSig, loading, isUpMd])

  // Paging returns you to the TOP of the results (Jules, 2026-08-23).
  //
  // The effect above deliberately ignores `page` because paging is not a new
  // search — but nothing else scrolled either, so clicking "2" left the
  // renter at the bottom of the rail staring at the pagination control with
  // page 2's first result somewhere above them. `behavior: 'auto'`, not
  // 'smooth': the content under the viewport has already been replaced, so
  // animating a scroll through it just shows a blur of the wrong results.
  const scrolledForPageRef = useRef(filters.page)
  useEffect(() => {
    if (filters.page === scrolledForPageRef.current) return
    scrolledForPageRef.current = filters.page
    const target = isUpMd ? refDesktop.current : refMobile.current
    target?.scrollTo({ top: 0, behavior: 'auto' })
  }, [filters.page, isUpMd])

  // Auto-open the mobile results drawer when the FIRST result set after
  // mount comes back empty. The entire recovery stack (on-demand hero,
  // CaptureLead, HostCTA) renders INSIDE the drawer — with it closed (the
  // default on /search arrivals), a zero-result landing showed only an
  // empty map + the 75px hint bar, and the recovery surfaces captured 1.8%
  // of dead-end sessions (marketing/FUNNELS.md F1, 2026-06-11 baseline).
  // Arrival-only on purpose: map pans / filter changes that hit zero keep
  // the hint-pill affordance — popping the drawer mid-exploration (or
  // re-opening after the user closed it) would fight the user.
  // The ref is consumed by the FIRST settled result set regardless of its
  // count — a later pan/filter that hits zero must NOT pop (only arrivals).
  const zeroArrivalHandledRef = useRef(false)
  useEffect(() => {
    if (zeroArrivalHandledRef.current) return
    if (!mounted) return
    if (emptyAndLoading || loading) return
    zeroArrivalHandledRef.current = true
    if (isUpMd) return
    if (totalLocations !== 0) return
    setIsSliderOpen(true)
  }, [
    mounted,
    isUpMd,
    emptyAndLoading,
    loading,
    totalLocations,
    setIsSliderOpen
  ])

  // Admin-only host-label overlay (sales team). Fed the map's marker set
  // plus the grid's current cards (the grid can briefly hold the prior
  // URL's listings during a refetch); for non-admins the provider is a
  // pass-through that fetches nothing. See AdminHostBadges.tsx.
  const badgeSourceLocations = useMemo(
    () => [...(allLocations ?? []), ...(locations ?? [])],
    [allLocations, locations]
  )

  return (
    <AdminHostBadgesProvider locations={badgeSourceLocations}>
      <NavBar
        isBottomDrawerOpen={isSliderOpen}
        setIsBottomDrawerOpen={setIsSliderOpen}
      />
      <Filters />

      <StyledContainer ref={sheetContainerRef}>
        <div
          ref={refDesktop}
          // `data-search-scroll-root`: the directory floor's IntersectionObserver
          // lookahead observes against this rail (see useInView.rootSelector) —
          // keep the marker on whichever element owns the results scroll.
          data-search-scroll-root
          className="hidden pt-2 pb-10 md:relative md:z-10 md:block md:h-full md:overflow-y-auto md:overscroll-contain md:shadow-[8px_0_24px_-4px_rgba(18,38,63,0.10)]"
        >
          <SearchList
            header={header}
            loading={emptyAndLoading}
            totalLocations={totalLocations}
            locations={locations}
            page={filters.page}
            pageCount={pageCount}
            directoryExpectedRows={directoryMapLocations.length}
            onDirectoryHoverChange={setHoveredDirectoryId}
            updateParams={updateParams}
            setSelectedLocation={setSelectedLocation}
            nearbySearchData={nearbySearchData}
            locationName={locationName}
            initialDirectory={initialDirectory}
            priorityImages={priorityImages}
            searchCenter={placeDetails ?? undefined}
          />
          {totalLocations > SEARCH_GRID_SIZE && (
            <FlexBox center pt={3}>
              <Pagination
                count={pageCount}
                onChange={(_, page) => updateParams({ page })}
                page={filters.page}
              />
            </FlexBox>
          )}
        </div>

        <MapContainer>
          {emptyAndLoading && (
            // Plain div with inline styles instead of <FlexBox absolute>.
            // FlexBox always includes `relative` as a base class, and
            // when `absolute` is also applied, Tailwind v4's compiled
            // CSS order causes `.relative` to win — leaving this
            // wrapper as `position: relative` (in flow), pushing the
            // Map element below it by its own height. That's the
            // "white block at the top of the map" we kept seeing.
            // Inline `position: absolute` has highest specificity and
            // is unambiguous.
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 5,
                pointerEvents: 'none'
              }}
            >
              <BaseButton
                variant="contained"
                customColor={Colors.black400}
                customContrastColor={Colors.white}
                onClick={() => {}}
                disabled={loading}
                rounded
                size="small"
                width={150}
                style={{
                  boxShadow: '0px 12px 12px -6px rgba(0, 82, 129, 0.2)',
                  pointerEvents: 'auto'
                }}
                suppressHydrationWarning
              >
                <div className="dot-pulse" />
              </BaseButton>
            </div>
          )}
          <MapWrapper
            selectedMarker={selectedLocation}
            onMarkerClick={(marker) => {
              setSelectedLocation(marker)
              if (marker) {
                trackMapInteraction(
                  'marker_open',
                  marker.defaultConfiguration?.id ?? marker.id
                )
                // Map-card impression — the missing denominator for map CTR.
                // Opening a marker shows its floating card, mirroring a list
                // card entering the viewport. Deduped per (session, configId)
                // and surface-scoped so it never blocks the list-card view.
                trackSearchResultView({
                  surface: 'map_card',
                  configId: marker.defaultConfiguration?.id,
                  locationId: marker.id,
                  coverPhotoId: marker.coverPhoto?.id,
                  badges: buildCardBadges(
                    marker,
                    getIsBrokerMode(marker.defaultConfiguration)
                  )
                })
              }
            }}
            center={initialCenter}
            zoom={mapZoom}
            maxZoom={MAX_PUBLIC_PIN_ZOOM}
            onIdleChange={({ lat, lng }, zoom) => {
              // Compare against latest known map position (fall back to
              // initialCenter on first idle). Use epsilon to ignore
              // floating-point drift from Google Maps.
              const EPS = 1e-5
              const ref = mapCenter ?? initialCenter
              const latSame = Math.abs(lat - ref.lat) < EPS
              const lngSame = Math.abs(lng - ref.lng) < EPS
              const zoomSame = zoom === mapZoom
              if (latSame && lngSame && zoomSame) return
              // Google's idle event fires only after map movement settles, so
              // this is already the debounced "user moved the map" signal — no
              // extra throttle needed. Zoom changed → 'zoom', else pan.
              trackMapInteraction(zoomSame ? 'pan' : 'zoom')
              setMapCenter({ lat, lng })
              if (!zoomSame) setMapZoom(zoom)
              if (urlPushTimerRef.current) {
                clearTimeout(urlPushTimerRef.current)
              }
              urlPushTimerRef.current = setTimeout(() => {
                shallowUpdateParams({
                  lat,
                  lng,
                  zoom,
                  placeId: undefined,
                  sliderOpen: isSliderOpen,
                  scrollY: undefined,
                  page: undefined,
                  ...mode
                })
              }, 300)
            }}
            markersButtons={allLocations}
            subordinateMarkers={directoryMapLocations}
            highlightedSubordinateId={hoveredDirectoryId}
            onSubordinateMarkerClick={(marker) => {
              const href = buildDirectoryFichaHref(marker)
              if (!href) return
              trackDirectoryCardClick({
                surface: 'search_map',
                locationId: marker.id
              })
              // New tab, matching a real listing card on desktop: the map is
              // the renter's place in the search and a pin must not take it
              // away. `noopener` because `window.open` otherwise hands the
              // opened page a reference back to this one.
              if (isUpMd) window.open(href, '_blank', 'noopener,noreferrer')
              else router.push(href)
            }}
            animation={true}
            floatingCard
            greedy={isUpMd}
            floatingCardPBM="pb-24"
            // Static preview while the SDK downloads — mobile only. Mobile
            // already paid for this request under the old `onScroll` code, so
            // it's cost-neutral there and it's where the slow connections
            // are; on desktop it would be a new billed Static Maps request on
            // our highest-traffic surface, buying only perceived paint on a
            // map that loads eagerly either way. Safe to branch on a
            // breakpoint here despite Hydration Safety #6: the preview lives
            // inside `MapLoader`, which `MapWrapper` renders only after
            // `hasMounted`, so none of it is server-rendered.
            previewWhileLoading={!isUpMd}
            // Don't download the ~1.5 MB SDK while the results drawer is
            // sitting on top of the map. On an SSG city/pillar arrival the
            // drawer starts OPEN (`sliderOpen` defaults to `!!initialFilters`
            // in FilterProvider), so the reader's first screen is the list —
            // and the static preview behind it is the LCP element. Letting
            // the SDK race it cost mobile p75 LCP ~1.1 s from 2026-08-26
            // (FCP flat over the same window, so it was contention, not
            // weight). Releases the moment the drawer is swiped down, which
            // is also the moment the map is worth having.
            //
            // On plain /search there are no `initialFilters`, so the drawer
            // starts closed, this is false from the first render and nothing
            // is deferred — the map IS that page's first screen.
            deferWhile={!isUpMd && isSliderOpen}
          />
        </MapContainer>

        {!isUpMd && (
          <StyledSwipeableDrawer
            container={container}
            anchor="bottom"
            open={isSliderOpen}
            onClose={() => setIsSliderOpen(false)}
            onOpen={() => setIsSliderOpen(true)}
            swipeAreaWidth={75}
            disableSwipeToOpen={false}
            disableBackdropTransition={!isIOS}
            disableDiscovery={!isIOS}
            // `disablePortal` puts the open sheet and its cards in the server
            // HTML — a portal renders nothing on the server, so phones saw no
            // list until hydration. Lab (4× CPU, Slow 4G, Monterrey): first
            // card on screen 11.5–16.2 s → 0.8–1.2 s; LCP moves to the sheet's
            // H1. Costs +63 kB HTML (+5 kB gzip); see the spec linked above.
            ModalProps={{ keepMounted: true, disablePortal: true }}
            keepMounted={true}
          >
            <div className="visible absolute inset-x-0 top-[-75px] flex h-[75px] flex-col items-center rounded-t-xl border-t border-ink-100/15 bg-grey-250 shadow-[0_-12px_32px_-16px_rgba(18,38,63,0.10)]">
              <div className="mt-2 h-[6px] w-[80px] rounded-full bg-ink-100/30" />
              <div
                className={`mt-2 flex flex-col items-center gap-0.5 transition-opacity duration-200 ${isSliderOpen ? 'opacity-0' : 'opacity-100'}`}
              >
                {!emptyAndLoading && totalLocations > 0 ? (
                  <>
                    <span className="rounded-full bg-blue-500/10 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
                      {/* Single text node — `{expr} spots` emits a `<!-- -->`
                          boundary comment that comment-stripping webviews
                          remove → #418. See CLAUDE.md "Trigger H". */}
                      {`${totalLocations > 50 ? '50+' : totalLocations} spots`}
                    </span>
                    <div className="flex items-center text-[11px] text-ink-200">
                      <KeyboardArrowUpIcon className="size-3.5 animate-bounce" />
                      <span>{t('SEARCH_MANAGER.SWIPE_UP_HINT')}</span>
                    </div>
                  </>
                ) : !emptyAndLoading && totalLocations === 0 ? (
                  <>
                    <span className="rounded-full bg-cream-300 px-2.5 py-0.5 text-xs font-semibold text-ink-200">
                      {t('SEARCH_MANAGER.NO_RESULTS_HINT')}
                    </span>
                    <div className="flex items-center text-[11px] text-ink-200">
                      <KeyboardArrowUpIcon className="size-3.5 animate-bounce" />
                      <span>{t('SEARCH_MANAGER.SWIPE_UP_ADJUST')}</span>
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            <div
              className="z-20 -mt-[50px] overflow-y-auto bg-grey-250"
              ref={refMobile}
              // Scroll-root marker for the directory floor's lookahead — see
              // the desktop rail's note above.
              data-search-scroll-root
            >
              <SearchList
                header={header}
                seoFooter={seoFooter}
                loading={emptyAndLoading}
                totalLocations={totalLocations}
                locations={locations}
                page={filters.page}
                pageCount={pageCount}
                directoryExpectedRows={directoryMapLocations.length}
                onDirectoryHoverChange={setHoveredDirectoryId}
                updateParams={updateParams}
                setSelectedLocation={setSelectedLocation}
                nearbySearchData={nearbySearchData}
                locationName={locationName}
                initialDirectory={initialDirectory}
                priorityImages={priorityImages}
                searchCenter={placeDetails ?? undefined}
              />
              {totalLocations > SEARCH_GRID_SIZE && (
                <FlexBox center py={3}>
                  <Pagination
                    count={pageCount}
                    onChange={(_, page) =>
                      updateParams({
                        page,
                        ...mode,
                        // Drawer is open when this pagination renders
                        // (it's inside the drawer). Pass `sliderOpen` so the
                        // destination — including a city/hood → /search hop
                        // where `initialFilters` flips and the default would
                        // be closed — lands with the drawer still open.
                        ...(isSliderOpen ? { sliderOpen: true } : {})
                      })
                    }
                    page={filters.page}
                  />
                </FlexBox>
              )}

              {/* Mount-gated: both already reach the server HTML through the
                  desktop copy below, and they sit far below the fold of the
                  sheet. SSR'ing them here too was 136 of the sheet's 200 kB. */}
              {mounted && seoFooter}

              {mounted && <Footer light />}
            </div>
          </StyledSwipeableDrawer>
        )}
      </StyledContainer>

      <Vsb md lg xl>
        {seoFooter}
        <Footer />
      </Vsb>
    </AdminHostBadgesProvider>
  )
}

export default React.memo(SearchManager)
